"use client";

import { useCallback } from "react";
import type { SessionCollab } from "./context";
import { selectLatestCollabCreatedAt } from "./session-date-selectors";
import {
  CHAT_PAGE_LIMIT,
  mergeCollabMap,
  selectCollabIds,
  selectNewIds,
  toSessionCollab,
} from "./session-data-provider-utils";
import type {
  SessionChatActionDependencies,
  SessionChatActions,
} from "./use-session-chat-actions.types";

type SessionProductCollabActions = Pick<
  SessionChatActions,
  "refreshCollabs" | "clearNewMessageIds"
>;

type UseSessionProductCollabActionsInput = Pick<
  SessionChatActionDependencies,
  | "api"
  | "sessionId"
  | "collabsByProduct"
  | "ownMessageIdsRef"
  | "setCollabs"
  | "setCollabsByProduct"
  | "setNewMessageIds"
  | "tagOwnCollabsAndAdvanceRead"
  | "updateProductsSeen"
  | "updateProductCollabsSinceTimestamp"
  | "pushMemberActivitiesFromCollabs"
>;

interface ApplyRefreshedProductCollabsInput
  extends Pick<
    UseSessionProductCollabActionsInput,
    | "ownMessageIdsRef"
    | "setCollabs"
    | "setCollabsByProduct"
    | "setNewMessageIds"
    | "tagOwnCollabsAndAdvanceRead"
    | "updateProductsSeen"
    | "pushMemberActivitiesFromCollabs"
  > {
  productId: string;
  items: readonly SessionCollab[];
  existingIds: readonly string[];
}

interface ExternalNewIdsInput {
  trulyNewIds: readonly string[];
  ownMessageIds: ReadonlySet<string>;
}

interface AddExternalNewMessagesInput {
  externalNewIds: readonly string[];
  setNewMessageIds: UseSessionProductCollabActionsInput["setNewMessageIds"];
}

export function useSessionProductCollabActions({
  api,
  sessionId,
  collabsByProduct,
  ownMessageIdsRef,
  setCollabs,
  setCollabsByProduct,
  setNewMessageIds,
  tagOwnCollabsAndAdvanceRead,
  updateProductsSeen,
  updateProductCollabsSinceTimestamp,
  pushMemberActivitiesFromCollabs,
}: UseSessionProductCollabActionsInput): SessionProductCollabActions {
  const refreshCollabs = useRefreshCollabsAction({
    api,
    sessionId,
    collabsByProduct,
    ownMessageIdsRef,
    setCollabs,
    setCollabsByProduct,
    setNewMessageIds,
    tagOwnCollabsAndAdvanceRead,
    updateProductsSeen,
    updateProductCollabsSinceTimestamp,
    pushMemberActivitiesFromCollabs,
  });
  const clearNewMessageIds = useClearNewMessageIdsAction({ setNewMessageIds });

  return {
    refreshCollabs,
    clearNewMessageIds,
  };
}

function useRefreshCollabsAction({
  api,
  sessionId,
  collabsByProduct,
  ownMessageIdsRef,
  setCollabs,
  setCollabsByProduct,
  setNewMessageIds,
  tagOwnCollabsAndAdvanceRead,
  updateProductsSeen,
  updateProductCollabsSinceTimestamp,
  pushMemberActivitiesFromCollabs,
}: UseSessionProductCollabActionsInput): SessionProductCollabActions["refreshCollabs"] {
  return useCallback(
    async (productId: string): Promise<void> => {
      try {
        const response = await api.sessions.byId(sessionId).collabs({
          query: {
            productId,
            limit: CHAT_PAGE_LIMIT,
          },
        });
        const items = response.items.map((collab) =>
          toSessionCollab({ collab }),
        );

        applyRefreshedProductCollabs({
          productId,
          items,
          existingIds: collabsByProduct[productId] || [],
          ownMessageIdsRef,
          setCollabs,
          setCollabsByProduct,
          setNewMessageIds,
          tagOwnCollabsAndAdvanceRead,
          updateProductsSeen,
          pushMemberActivitiesFromCollabs,
        });

        updateProductCollabsSinceTimestamp({
          productId,
          collabsSince: new Date().toISOString(),
        });
      } catch (error) {}
    },
    [
      api,
      collabsByProduct,
      ownMessageIdsRef,
      pushMemberActivitiesFromCollabs,
      sessionId,
      setCollabs,
      setCollabsByProduct,
      setNewMessageIds,
      tagOwnCollabsAndAdvanceRead,
      updateProductCollabsSinceTimestamp,
      updateProductsSeen,
    ],
  );
}

function useClearNewMessageIdsAction({
  setNewMessageIds,
}: Pick<
  UseSessionProductCollabActionsInput,
  "setNewMessageIds"
>): SessionProductCollabActions["clearNewMessageIds"] {
  return useCallback(
    (messageIds: string[]): void => {
      setNewMessageIds((prev) => {
        const updated = new Set(prev);
        messageIds.forEach((id) => updated.delete(id));
        return updated;
      });
    },
    [setNewMessageIds],
  );
}

function applyRefreshedProductCollabs({
  productId,
  items,
  existingIds,
  ownMessageIdsRef,
  setCollabs,
  setCollabsByProduct,
  setNewMessageIds,
  tagOwnCollabsAndAdvanceRead,
  updateProductsSeen,
  pushMemberActivitiesFromCollabs,
}: ApplyRefreshedProductCollabsInput): void {
  if (items.length === 0) {
    return;
  }

  mergeProductCollabState({
    productId,
    items,
    setCollabs,
    setCollabsByProduct,
  });
  tagOwnCollabsAndAdvanceRead({ productId, collabs: items });
  notifyProductCollabArrivals({
    items,
    existingIds,
    ownMessageIds: ownMessageIdsRef.current,
    setNewMessageIds,
    pushMemberActivitiesFromCollabs,
  });
  updateProductsSeen({
    productId,
    patch: {
      lastAddedCollabs: selectLatestCollabCreatedAt({ items }),
    },
  });
}

function mergeProductCollabState({
  productId,
  items,
  setCollabs,
  setCollabsByProduct,
}: Pick<
  ApplyRefreshedProductCollabsInput,
  "productId" | "items" | "setCollabs" | "setCollabsByProduct"
>): void {
  setCollabs((prev) =>
    mergeCollabMap({
      previous: prev,
      items,
    }),
  );

  setCollabsByProduct((prev) => {
    const existingProductIds = prev[productId] || [];
    const incomingIds = selectCollabIds(items);
    return {
      ...prev,
      [productId]: [
        ...existingProductIds,
        ...selectNewIds({
          incomingIds,
          existingIds: existingProductIds,
        }),
      ],
    };
  });
}

function notifyProductCollabArrivals({
  items,
  existingIds,
  ownMessageIds,
  setNewMessageIds,
  pushMemberActivitiesFromCollabs,
}: Pick<
  ApplyRefreshedProductCollabsInput,
  "items" | "existingIds" | "setNewMessageIds" | "pushMemberActivitiesFromCollabs"
> & {
  ownMessageIds: ReadonlySet<string>;
}): void {
  const trulyNewIds = items
    .map((collab) => collab.id)
    .filter((id) => !existingIds.includes(id));
  const externalNewIds = selectExternalNewIds({
    trulyNewIds,
    ownMessageIds,
  });
  addExternalNewMessageIds({
    externalNewIds,
    setNewMessageIds,
  });
  pushMemberActivitiesFromCollabs({
    collabs: items,
    arrivalIds: new Set(externalNewIds),
  });
}

function selectExternalNewIds({
  trulyNewIds,
  ownMessageIds,
}: ExternalNewIdsInput): string[] {
  return trulyNewIds.filter((id) => !ownMessageIds.has(id));
}

function addExternalNewMessageIds({
  externalNewIds,
  setNewMessageIds,
}: AddExternalNewMessagesInput): void {
  if (externalNewIds.length === 0) {
    return;
  }

  setNewMessageIds((prev) => {
    const updated = new Set(prev);
    externalNewIds.forEach((id) => updated.add(id));
    return updated;
  });
}
